import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  ListTargetProbeLegsResponse,
  ListTargetProbeStateResponse,
  ProbeTargetNowRequest,
  ProbeTargetNowResponse,
  ResetTargetProbeRequest,
  ResetTargetProbeResponse,
  TryTargetProbeRequest,
  TryTargetProbeResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';
import {
  listTargetProbes,
  probeTargetNow,
  resetTargetProbe,
  tryTargetProbe,
} from './targetProbes.ts';

export const TARGET_PROBES_KEY = ['target-probes'] as const;
const TARGET_PROBES_REFETCH_INTERVAL_MS = 15_000;

const TARGET_PROBE_LEGS_STALE_MS = 30_000;

export function useTargetProbeLegs(): UseQueryResult<ListTargetProbeLegsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...TARGET_PROBES_KEY, 'legs'],
    queryFn: ({ signal }) => opsApiRequest<ListTargetProbeLegsResponse>(
      '/v1/ops/target-probes/legs',
      { signal },
      fetchImpl,
    ),
    staleTime: TARGET_PROBE_LEGS_STALE_MS,
  });
}

export function useTargetProbes(): UseQueryResult<ListTargetProbeStateResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: TARGET_PROBES_KEY,
    queryFn: ({ signal }) => listTargetProbes(fetchImpl, signal),
    refetchInterval: TARGET_PROBES_REFETCH_INTERVAL_MS,
  });
}

export function useResetTargetProbe(): UseMutationResult<
  ResetTargetProbeResponse,
  Error,
  ResetTargetProbeRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => resetTargetProbe(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TARGET_PROBES_KEY });
    },
  });
}

export function useProbeTargetNow(): UseMutationResult<
  ProbeTargetNowResponse,
  Error,
  ProbeTargetNowRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => probeTargetNow(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TARGET_PROBES_KEY });
    },
  });
}

/**
 * 试打: no cache invalidation on purpose — the shot writes no probe state,
 * so nothing on the page may change because of it.
 */
export function useTryTargetProbe(): UseMutationResult<
  TryTargetProbeResponse,
  Error,
  TryTargetProbeRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body) => tryTargetProbe(body, fetchImpl),
  });
}
